import { StatusModal } from '@/components/StatusModal';
import { MaterialAddWizard } from '@/components/sklad/MaterialAddWizard';
import { MaterialItemModal } from '@/components/sklad/MaterialItemModal';
import { MaterialStockModal } from '@/components/sklad/MaterialStockModal';
import { ZipProblemsPanel } from '@/components/sklad/ZipProblemsPanel';
import {
  LEVEL_BADGE,
  type Material,
  NO_CATEGORY,
  type StockLevel,
  type ZipProblem,
  formatAmount,
  materialLabel,
  stockLevel,
} from '@/components/sklad/materialTypes';
import { formatDate, matches, tokenize } from '@/components/sklad/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useMemo, useState } from 'react';

const CARD = 'bg-surface rounded-xl shadow-card';
const INPUT =
  'text-sm text-text bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all';

type Filter = 'all' | 'attention';

/**
 * Sklad drobného materiálu — zipy, knoflíky, gumy… Stav na položku, minimum,
 * co je na cestě a co je rezervované pro výrobu (zipy podle kusovníku).
 * Stav se hodnotí z volného množství = sklad − rezervace.
 */
export function SkladMaterialuPage() {
  const [items, setItems] = useState<Material[] | null>(null);
  const [problemy, setProblemy] = useState<ZipProblem[]>([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Material | 'new' | null>(null);
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState<{ item: Material; mode: 'issue' | 'adjust' } | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ items: Material[]; problemy: ZipProblem[] }>(
        '/api/material/items',
      );
      setItems(d.items);
      setProblemy(d.problemy);
    } catch (e: unknown) {
      setItems([]);
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Chyba načítání', kind: 'error' });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const active = useMemo(() => (items ?? []).filter((i) => !i.archived_at), [items]);

  const counts = useMemo(() => {
    const c: Record<StockLevel, number> = { short: 0, out: 0, low: 0, covered: 0, ok: 0, idle: 0 };
    for (const i of active) c[stockLevel(i)]++;
    return c;
  }, [active]);

  const categories = useMemo(
    () =>
      [...new Set(active.map((i) => i.category).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'cs'),
      ),
    [active],
  );
  const suppliers = useMemo(
    () =>
      [...new Set(active.map((i) => i.supplier).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'cs'),
      ),
    [active],
  );

  // Seskupení podle druhu, v rámci druhu pořadí z API (název, barva, velikost).
  const groups = useMemo(() => {
    const tokens = tokenize(query);
    const shown = (items ?? []).filter((i) => {
      if (!showArchived && i.archived_at) return false;
      if (filter === 'attention') {
        const lvl = stockLevel(i);
        if (lvl !== 'short' && lvl !== 'out' && lvl !== 'low') return false;
      }
      return matches(
        `${i.category} ${i.name} ${i.color} ${i.size} ${i.code} ${i.supplier}`,
        tokens,
      );
    });
    const map = new Map<string, Material[]>();
    for (const i of shown) {
      const key = i.category || NO_CATEGORY;
      const list = map.get(key) ?? [];
      list.push(i);
      map.set(key, list);
    }
    return [...map.entries()];
  }, [items, query, filter, showArchived]);

  const attention = counts.short + counts.out + counts.low;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text">Sklad drobného materiálu</h1>
          <p className="text-sm text-text-secondary mt-1">
            Zipy, knoflíky, gumy a další — stav, minimální zásoba a co je objednané
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setEditing('new')}
            title="Založit do katalogu úplně nový druh materiálu (zipy přibývají samy z Databáze zipů)"
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors"
          >
            Nová položka
          </button>
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={!items || items.length === 0}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 cursor-pointer transition-colors disabled:opacity-50"
          >
            + Naskladnit
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        {(['short', 'out', 'low', 'covered', 'ok'] as const).map((lvl) => (
          <div key={lvl} className={`${CARD} px-4 py-3`}>
            <div className="text-xs font-semibold text-text-muted uppercase tracking-wider">
              {LEVEL_BADGE[lvl].label}
            </div>
            <div
              className={`text-2xl font-bold mt-0.5 ${
                (lvl === 'out' || lvl === 'short') && counts[lvl] > 0
                  ? 'text-danger'
                  : lvl === 'low' && counts.low > 0
                    ? 'text-warning'
                    : 'text-text'
              }`}
            >
              {counts[lvl]}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Hledat druh, název, barvu, velikost, kód…"
          className={`${INPUT} w-full sm:w-80`}
        />
        <div className="flex gap-1 bg-bg rounded-lg p-1">
          {(
            [
              ['all', 'Vše'],
              ['attention', `K doobjednání (${attention})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`text-sm font-semibold px-3 py-1.5 rounded-md cursor-pointer transition-colors ${
                filter === key ? 'bg-surface text-primary shadow-card' : 'text-text-secondary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="w-4 h-4 rounded border-border cursor-pointer accent-primary"
          />
          Zobrazit archivované
        </label>
      </div>

      <ZipProblemsPanel problemy={problemy} />

      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg text-left text-xs font-semibold text-text-muted uppercase tracking-wider">
              <th className="px-5 py-3">Položka</th>
              <th className="px-4 py-3 text-right">Skladem</th>
              <th
                className="px-4 py-3 text-right"
                title="Kusy v Zadání do výroby (podle kusovníku)"
              >
                Rezervováno
              </th>
              <th className="px-4 py-3 text-right">Volné</th>
              <th className="px-4 py-3 text-right">Minimum</th>
              <th className="px-4 py-3">Na cestě</th>
              <th className="px-4 py-3">Stav</th>
              <th className="px-5 py-3 text-right">Akce</th>
            </tr>
          </thead>
          <tbody>
            {items === null && (
              <tr>
                <td colSpan={8} className="px-5 py-8 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            )}
            {items !== null && groups.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-8 text-center text-text-muted">
                  {items.length === 0
                    ? 'Zatím žádný materiál. Začni tlačítkem „+ Nová položka".'
                    : filter === 'attention'
                      ? 'Nic nechybí. 🎉'
                      : 'Nic nenalezeno.'}
                </td>
              </tr>
            )}
            {groups.map(([category, list]) => (
              <CategoryRows
                key={category}
                category={category}
                list={list}
                onEdit={setEditing}
                onMove={(item, mode) => setMoving({ item, mode })}
              />
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <MaterialItemModal
          item={editing === 'new' ? null : editing}
          categories={categories}
          suppliers={suppliers}
          onClose={() => setEditing(null)}
          onError={(msg) => setToast({ msg, kind: 'error' })}
          onSaved={async (msg) => {
            setEditing(null);
            await load();
            setToast({ msg, kind: 'success' });
          }}
        />
      )}

      {adding && items && (
        <MaterialAddWizard
          items={items}
          onClose={() => setAdding(false)}
          onError={(msg) => setToast({ msg, kind: 'error' })}
          onSaved={async (msg) => {
            setAdding(false);
            await load();
            setToast({ msg, kind: 'success' });
          }}
        />
      )}

      {moving && (
        <MaterialStockModal
          item={moving.item}
          mode={moving.mode}
          onClose={() => setMoving(null)}
          onError={(msg) => setToast({ msg, kind: 'error' })}
          onSaved={async (msg) => {
            setMoving(null);
            await load();
            setToast({ msg, kind: 'success' });
          }}
        />
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

function CategoryRows({
  category,
  list,
  onEdit,
  onMove,
}: {
  category: string;
  list: Material[];
  onEdit: (m: Material) => void;
  onMove: (m: Material, mode: 'issue' | 'adjust') => void;
}) {
  return (
    <>
      <tr className="bg-bg border-b border-border-light">
        <td
          colSpan={8}
          className="px-5 py-2 text-xs font-bold text-text-secondary uppercase tracking-wider"
        >
          {category} <span className="text-text-muted font-semibold">({list.length})</span>
        </td>
      </tr>
      {list.map((m) => {
        const lvl = stockLevel(m);
        const badge = LEVEL_BADGE[lvl];
        return (
          <tr
            key={m.id}
            className={`border-b border-border-light hover:bg-surface-hover transition-colors ${
              m.archived_at ? 'opacity-50' : ''
            }`}
          >
            <td className="px-5 py-3">
              <div className="flex items-center gap-2 text-text font-medium">
                {m.color_hex && (
                  <span
                    // Barva je data položky — Tailwind třídu pro libovolný hex nemá.
                    style={{ backgroundColor: m.color_hex }}
                    className="inline-block h-4 w-4 shrink-0 rounded-full border border-border"
                  />
                )}
                {materialLabel(m)}
              </div>
              <div className="text-xs text-text-muted">
                {[m.code, m.supplier, m.note].filter(Boolean).join(' · ') || '—'}
              </div>
            </td>
            <td className="px-4 py-3 text-right font-semibold text-text whitespace-nowrap">
              {formatAmount(m.stock_qty, m.unit)}
            </td>
            <td
              className="px-4 py-3 text-right text-text-secondary whitespace-nowrap"
              title={`Rozpracované zakázky: ${m.reserved_detail.zakazky} ${m.unit}\nK nastříhání (plán): ${m.reserved_detail.plan} ${m.unit}`}
            >
              {m.reserved > 0 ? formatAmount(m.reserved, m.unit) : '—'}
            </td>
            <td
              className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${
                m.stock_qty - m.reserved < 0 ? 'text-danger' : 'text-text'
              }`}
            >
              {formatAmount(m.stock_qty - m.reserved, m.unit)}
            </td>
            <td className="px-4 py-3 text-right text-text-secondary whitespace-nowrap">
              {m.min_qty > 0 ? formatAmount(m.min_qty, m.unit) : '—'}
            </td>
            <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
              {m.on_order > 0 ? (
                <>
                  {formatAmount(m.on_order, m.unit)}
                  {m.next_expected_at && (
                    <span className="text-xs text-text-muted">
                      {' '}
                      · {formatDate(m.next_expected_at)}
                    </span>
                  )}
                </>
              ) : (
                '—'
              )}
            </td>
            <td className="px-4 py-3">
              {m.archived_at ? (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-bg text-text-muted">
                  Archiv
                </span>
              ) : (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${badge.cls}`}>
                  {badge.label}
                </span>
              )}
            </td>
            <td className="px-5 py-3">
              <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                <ActionButton onClick={() => onMove(m, 'issue')}>Výdej</ActionButton>
                <ActionButton onClick={() => onMove(m, 'adjust')}>Inventura</ActionButton>
                <ActionButton onClick={() => onEdit(m)}>Upravit</ActionButton>
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}

function ActionButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-semibold text-text-secondary border border-border rounded-lg px-2.5 py-1.5 hover:bg-surface-hover hover:text-primary cursor-pointer transition-colors"
    >
      {children}
    </button>
  );
}
